import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Siding Installation',
    description: 'Quality siding installation and repairs for lasting protection.'
  },
  {
    title: 'Window Replacement',
    description: 'Energy-efficient window solutions for your home.'
  },
  {
    title: 'Door Installation',
    description: 'Expert installation of entry, patio, and storm doors.'
  },
  {
    title: 'Roof Repairs',
    description: 'Professional roofing services to protect your home.'
  },
  {
    title: 'Gutter Systems',
    description: 'Complete gutter installation and maintenance services.'
  },
  {
    title: 'Exterior Painting',
    description: 'Professional painting to enhance and protect your home.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale Exterior'
  },
  {
    url: 'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Home'
  },
  {
    url: 'https://images.unsplash.com/photo-1625602812206-5ec545ca1231?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Residence'
  }
];

export function ExteriorImprovementsPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Exterior Home Improvements in Westchester County | Golden Touch</title>
        <meta name="description" content="Professional exterior home improvement services in Westchester County & Greenwich. Expert siding, windows, doors, and roofing services to protect and enhance your home." />
        <meta name="keywords" content="exterior improvements Westchester, siding installation Greenwich, window replacement Scarsdale, roofing contractor" />
        <link rel="canonical" href="https://renovationwestchester.com/services/exterior-improvements" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Exterior Home Improvements in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Protect and enhance your home's exterior with our comprehensive improvement services. 
                From siding and windows to roofing and gutters, we ensure quality installation and 
                lasting protection.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link 
                to="/consultation"
                className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Exterior Improvements in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Our Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Improve Your Home's Exterior?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Let's discuss your exterior improvement needs. Our team is ready to help protect 
              and enhance your home with quality materials and expert installation.
            </p>
            <Link 
              to="/consultation"
              className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}